import PartialRectIcon from '@mui/icons-material/GpsOff';
import {Box, Card, Stack, Tooltip, Typography} from '@mui/material';
import PropTypes from 'prop-types';
import React from 'react';
import {useDrag} from 'react-dnd';
import {useTranslation} from 'react-i18next';
import {useDispatch, useSelector} from 'react-redux';

import {
  addResultToOrder,
  addResultToView,
  removeResultFromOrder,
  removeResultFromView,
} from '@explorer/actions/results';
import {setActiveResultId} from '@explorer/actions/sidebar';
import {trackEvent} from '@explorer/analytics';
import {
  EyeBadgeIcon,
  OrderToggleButton,
  ViewItemsButton,
} from '@explorer/components/items-panel/item-components';
import {
  CARD_LAYOUT_STYLES,
  THUMBNAIL_DIMENSIONS_V2,
} from '@explorer/components/items-panel/styles';
import ItemType from '@explorer/components/ui/item-type';
import {
  AreaCoverageIndicator,
  IndicatorsContainer,
  ItemIndicator,
  PixelResolutionIndicator,
} from '@explorer/components/ui/metadata-indicators';
import ThumbnailCard from '@explorer/components/ui/thumbnail-card';
import Thumb from '@explorer/components/ui/thumbnail-stickymap';
import {COMPARE_TYPES, UI_MODES} from '@explorer/constants';
import {getSearchGeometryGeoJson} from '@explorer/selectors/search';
import {getResolution} from '@explorer/util/items';
import {
  hasPartialRectification,
  isCompareSubject,
} from '@explorer/util/results';

const ResultItem = ({resultId, canUserDownload}) => {
  const {t} = useTranslation();
  const dispatch = useDispatch();

  const result = useSelector(state => state.results.resultsById[resultId]);
  const isComparing = useSelector(state => state.ui.mode === UI_MODES.COMPARE);
  const isComparingSubject = useSelector(state =>
    isCompareSubject(state.compare.subjects, resultId)
  );
  const isViewSelected = useSelector(state =>
    state.results.viewResults.includes(resultId)
  );
  const isOrderSelected = useSelector(state =>
    state.results.orderResults.includes(resultId)
  );
  const clipGeometry = useSelector(getSearchGeometryGeoJson);

  const [{isDragging}, dragRef] = useDrag(
    () => ({
      type: COMPARE_TYPES.RESULT,
      item: result,
      canDrag: isComparing,
      collect: monitor => ({
        isDragging: monitor.isDragging(),
      }),
    }),
    [isComparing]
  );

  const onOrderButtonClick = () => {
    isOrderSelected
      ? dispatch(removeResultFromOrder({resultId}))
      : dispatch(addResultToOrder({resultId}));

    trackEvent('Result Item V2 - Order button click', {
      enabled: isOrderSelected,
    });
  };

  const onThumbButtonClick = () => {
    isViewSelected
      ? dispatch(removeResultFromView({resultId}))
      : dispatch(addResultToView({resultId}));

    trackEvent('Result Item V2 - View button click', {
      enabled: isViewSelected,
    });
  };

  const onViewScenesClick = () => {
    dispatch(setActiveResultId(resultId));

    trackEvent('Result Item V2 - View items button click');
  };

  if (!result) {
    return false;
  }

  const items = result.properties.items;
  const canOrder = canUserDownload && result.properties.availableItems > 0;
  const resolution = getResolution(items[0]);

  return (
    <Card
      ref={dragRef}
      sx={{
        cursor: !isComparing ? 'default' : isDragging ? 'grabbing' : 'grab',
        transform: 'translate3d(0, 0, 0)',
        backgroundColor: theme =>
          isComparingSubject && isComparing
            ? theme.palette.primary.lighter
            : theme.palette.background.paper,
        ...CARD_LAYOUT_STYLES,
        paddingX: 1,
        paddingY: 1,
        borderBottom: '1px solid',
        borderColor: 'divider',
      }}
      elevation={0}
      square
      draggable={isComparing}
    >
      <ThumbnailCard
        button={!isComparing}
        aria-label={
          isViewSelected
            ? t('resultItem.removeFromMap', {
                count: items.length,
              })
            : t('resultItem.addToMap', {
                count: items.length,
              })
        }
        thumbnail={
          <Thumb
            ariaLabel={`Thumbnail of scenes for ${t('common:date.long', {
              date: result.properties.acquired,
            })}`}
            clip={clipGeometry}
            height={THUMBNAIL_DIMENSIONS_V2}
            items={items}
            width={THUMBNAIL_DIMENSIONS_V2}
            flexBasis={THUMBNAIL_DIMENSIONS_V2}
          />
        }
        onClick={onThumbButtonClick}
        selected={!isComparing && isViewSelected}
        sx={{
          marginRight: 1,
          width: THUMBNAIL_DIMENSIONS_V2,
          gridArea: 'thumb',
        }}
        data-qe="view-on-map-button"
      >
        <EyeBadgeIcon isViewSelected={isViewSelected} />
      </ThumbnailCard>
      <Typography
        variant="subtitle1"
        sx={{
          gridArea: 'title',
        }}
      >
        {t('common:date.long', {
          date: result.properties.acquired,
        })}
      </Typography>
      <Box
        sx={{
          gridArea: 'content',
          display: 'flex',
          flex: 1,
          flexDirection: 'column',
        }}
      >
        <Box
          sx={{
            flex: 1,
          }}
        >
          <ItemType
            itemType={result.properties.item_type}
            typographyProps={{variant: 'body2'}}
          />
          <IndicatorsContainer marginTop={1}>
            <PixelResolutionIndicator resolution={resolution} />
            {result.properties.coverage !== undefined && (
              <AreaCoverageIndicator coverage={result.properties.coverage} />
            )}
            {hasPartialRectification(items) && (
              <ItemIndicator
                tooltip={t('resultItem.partiallyRectified')}
                icon={<PartialRectIcon />}
              />
            )}
          </IndicatorsContainer>
        </Box>
      </Box>
      <Stack
        direction="row"
        spacing={1}
        sx={{
          gridArea: 'action',
          justifyContent: 'flex-end',
        }}
      >
        <Tooltip
          title={
            canOrder
              ? t(
                  `resultItem.${
                    isOrderSelected ? 'removeFromOrder' : 'addToOrder'
                  }`,
                  {
                    count: items.length,
                  }
                )
              : t('resultItem.orderNoPermissions')
          }
        >
          <OrderToggleButton
            disabled={!canOrder}
            selected={isOrderSelected}
            value="orderResult"
            onClick={onOrderButtonClick}
          />
        </Tooltip>
      </Stack>
      <Box
        sx={{
          display: 'flex',
          gridArea: 'view',
          justifyContent: 'flex-end',
        }}
      >
        <ViewItemsButton
          itemCount={items.length}
          onClick={onViewScenesClick}
          data-qe="view-details-button"
        />
      </Box>
    </Card>
  );
};

ResultItem.propTypes = {
  resultId: PropTypes.string.isRequired,
  canUserDownload: PropTypes.bool,
};

export default ResultItem;
